import { showErrorToastFullScreen } from './toastUtils';

/**
 * Stable, machine-readable identifiers returned by the backend in the
 * `error_code` field of the JSON error envelope. Branch UI on these
 * instead of string-matching `description`. May be `null` for legacy
 * unclassified raise sites — fall back to status_code / description.
 */
export const ApiErrorCode = {
  // Auth / Gmail
  GmailAuthExpired: 'gmail_auth_expired',
  GmailAuthError: 'gmail_auth_error',
  GmailSendError: 'gmail_send_error',
  // Raised by the send path and, since the Auto-Apply entry gate, by
  // PUT /users/me/settings when a user tries to switch Auto-Apply on without a
  // Gmail grant that can send.
  GmailNotConnected: 'gmail_not_connected',
  GmailSendScopeMissing: 'gmail_send_scope_missing',
  // Bot protection. Returned as a 403 by BOTH POST /users/registrations and
  // POST /oauth/token when the Cloudflare Turnstile token is missing, expired,
  // already redeemed, or fails siteverify. It is the only code that should make
  // an auth page SAY a verification failed — a plain AccessDeniedError (role
  // failure) carries a different code and is a different message.
  //
  // It is NOT the only failure that should reset the widget. Both endpoints
  // verify before they do anything else, so a 401, a 409 or a 422 has spent the
  // token too; see isTurnstileTokenSpent below, which owns that decision.
  TurnstileVerificationFailed: 'turnstile_verification_failed',
  // The two "our side broke" outcomes. 500 (a blank TURNSTILE_SECRET_KEY — a
  // deploy error, so it is NOT Sentry-suppressed — or siteverify rejecting the
  // secret itself) and 503 (siteverify unreachable, or answering
  // `internal-error`; fail-closed by design). They share a description on
  // purpose, so the code is the only thing telling them apart. Neither proves
  // the token survived: each covers a before-siteverify case AND an
  // after-siteverify one, so both still reset the widget — see
  // isTurnstileTokenSpent below.
  TurnstileMisconfigured: 'turnstile_misconfigured',
  TurnstileUnreachable: 'turnstile_unreachable',
  // Data / persistence
  DatabaseRecordNotFound: 'database_record_not_found_error',
  CacheRecordNotFound: 'cache_record_not_found_error',
  ResumeNotFound: 'resume_not_found_error',
  UnsupportedResumeFormat: 'unsupported_resume_format_error',
  BlobNotFound: 'blob_not_found_error',
  DatabaseIntegrity: 'database_integrity_error',
  DatabaseInvalidSortField: 'database_invalid_sort_field_error',
  DatabaseError: 'database_error',
  // Searches — single-create 409 branching (batch expresses these via
  // skipped_limit / skipped_duplicate + the quota object instead).
  SearchLimitReached: 'search_limit_reached',
  SearchAlreadyExists: 'search_already_exists',
  // Email (transactional)
  EmailResendLimitExceeded: 'email_resend_limit_exceeded_error',
  EmailError: 'email_error',
  EmailHttpError: 'email_http_error',
  // Recipient verification (409s from approve and cover-letter regenerate).
  // Not sendable: the address was rejected; nothing was sent and another
  // contact is being looked for. Check pending: the address is being re-checked.
  // Contact changed: the draft was written for a contact the opportunity no
  // longer has. Client outdated: draft text was sent without the contact it was
  // written for (an old tab) — the description asks the user to reload.
  ContactNotSendable: 'contact_not_sendable',
  ContactCheckPending: 'contact_check_pending',
  ContactChanged: 'contact_changed',
  ClientOutdated: 'client_outdated',
  // LLM
  LlmFailed: 'llm_failed_error',
  LlmParsingFailed: 'llm_parsing_failed_error',
} as const;

// The const-object-plus-companion-type idiom: TypeScript keeps types and values
// in separate declaration spaces, so `ApiErrorCode` names the frozen map above
// AND the union of its values, and both `ApiErrorCode.GmailAuthExpired` and
// `code: ApiErrorCode` resolve. `@typescript-eslint/no-redeclare` only ignores
// the merges TypeScript calls declaration merging (interface+interface,
// class+namespace, ...), which does not include type-alias+const, so it reports
// this legal pair. Renaming either half would rewrite the 39 references
// across 9 files to buy nothing.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ApiErrorCode = typeof ApiErrorCode[keyof typeof ApiErrorCode];

export interface ApiErrorEnvelope {
  status_code: number;
  error_code: ApiErrorCode | string | null;
  description: string;
  detail?: unknown;
}

/**
 * Extract the backend `error_code` from an axios error. Returns null
 * for non-axios errors, network errors, or unclassified responses.
 */
export function getApiErrorCode(error: any): ApiErrorCode | string | null {
  return error?.response?.data?.error_code ?? null;
}

/**
 * Did this failure already redeem the Turnstile token that was sent with it?
 *
 * Turnstile tokens are single-use and BOTH auth endpoints verify before they do
 * anything else — `oauth/router.py` verifies before `authenticate_and_get_user`,
 * `users/router.py` verifies before `register_user`. So a 401 for a mistyped
 * password, a 409 for a duplicate email and a 422 for a bad phone have all spent
 * the token already: leaving it in place means the corrected second attempt is
 * replayed as `timeout-or-duplicate` and fails too, blaming verification for a
 * typo.
 *
 * The rule is therefore: ANY HTTP response resets. That includes the two "our
 * side broke" codes, which do NOT prove the token survived — each conflates a
 * before-siteverify case with an after-siteverify one:
 *
 *   - `turnstile_misconfigured` (500) is raised for a blank secret, before any
 *     siteverify call and with the token genuinely unspent — but ALSO when
 *     siteverify itself answers `success: false` with `invalid-input-secret`,
 *     `missing-input-secret` or `bad-request`. There, siteverify received and
 *     processed the token, so it IS consumed.
 *
 *   - `turnstile_unreachable` (503) is raised on a network failure or timeout,
 *     where the request may or may not have reached Cloudflare — and ALSO on
 *     siteverify's `internal-error`, where it definitely did.
 *
 * Preserving the token on those is what breaks a brief Cloudflare outage or a
 * secret fixed mid-session: the retry replays the token, siteverify answers
 * `timeout-or-duplicate`, and the user gets a 403 reading "We couldn't confirm
 * that verification" — an extra round trip and a misleading message for a fault
 * that was never theirs. Exactly what this function exists to prevent.
 *
 * One case still skips the reset: no HTTP response at all. That is a HEURISTIC,
 * not proof, and knowingly wrong in one case: a server that fully processes the
 * request — redeeming the token — and then drops the socket arrives as
 * `code: 'ECONNRESET'`, `response: undefined`, so this answers false for a token
 * that is in fact spent, and the user pays one extra failed submit. Accepted
 * because the alternative is worse and far more common: resetting on a genuine
 * offline failure throws away a good token to start a challenge that cannot
 * complete until connectivity returns, leaving the user stuck behind an
 * unsolvable widget rather than one failed retry.
 *
 * The asymmetry settles everything else: a needless reset costs a silent
 * managed-mode re-issue, a missed one costs a guaranteed extra round trip and a
 * misleading message.
 *
 * Resetting is token hygiene and is SILENT. Whether to SAY a verification failed
 * is a separate decision the pages make on `turnstile_verification_failed`
 * alone — a 500 or a 503 resets the widget without ever telling the user their
 * verification failed.
 */
export function isTurnstileTokenSpent(error: any): boolean {
  return Boolean(error?.response);
}

/**
 * Fired on the window when the backend returns `gmail_auth_expired`.
 * Listeners (Settings, OpportunityDrawer) should drop their local
 * "connected" state so the existing reconnect UI takes over.
 */
export const GMAIL_AUTH_EXPIRED_EVENT = 'gmail:auth-expired';

/**
 * Error messages for specific HTTP status codes
 */
const HTTP_ERROR_MESSAGES: Record<number, string> = {
  401: 'Session expired. Please sign in again.',
  404: 'The requested resource was not found.',
  409: 'A conflict occurred. Please try again.',
  422: 'Invalid data provided.',
};

/**
 * Get a user-friendly error message based on the error response.
 * Priority: server-provided `description` (the user-facing message in
 * the backend's error envelope) > FastAPI validation array in `detail`
 * (422 only) > status-code default. `detail` outside of 422 is debug
 * context (e.g. "{'user_id': '...'}") and is intentionally not surfaced.
 */
export function getApiErrorMessage(error: any): string {
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    switch (status) {
      case 401:
        return HTTP_ERROR_MESSAGES[401];
      case 404:
        return data?.description || HTTP_ERROR_MESSAGES[404];
      case 409:
        return data?.description || HTTP_ERROR_MESSAGES[409];
      case 422:
        if (Array.isArray(data?.detail)) {
          const firstError = data.detail[0];
          if (firstError?.loc && firstError?.msg) {
            return `${firstError.loc[firstError.loc.length - 1]}: ${firstError.msg}`;
          }
          return data.detail.map((err: any) => err.msg).join(', ');
        }
        return data?.description || HTTP_ERROR_MESSAGES[422];
      default:
        if (data?.description) {
          return data.description;
        }
        return 'An unexpected error occurred. Please try again.';
    }
  }

  // Handle network errors (request made but no response received)
  if (error.request) {
    return 'Network error. Please check your connection.';
  }

  // Handle other errors
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Check if error is a 409 Conflict error
 */
export function isConflictError(error: any): boolean {
  return error.response?.status === 409;
}

/**
 * Check if error is a 404 Not Found error.
 * On the resume-review endpoint this means the cached upload expired
 * (`resume_not_found_error`) → prompt the user to re-upload.
 */
export function isNotFoundError(error: any): boolean {
  return error?.response?.status === 404;
}

/**
 * Check if error is a 500 Server error.
 * On the resume-review endpoint this means the LLM failed after retries
 * → offer "try again" and keep "proceed without review" available.
 */
export function isServerError(error: any): boolean {
  return error?.response?.status === 500;
}

/**
 * Legacy function - kept for backwards compatibility
 * @deprecated Use getApiErrorMessage instead
 */
export function getErrorMessage(error: any): string | null {
    // Check if the error has a response property
    if (error.response) {
      // Get the response text
      // Check if the "description" key exists in the parsed data
      if (error.response.data.hasOwnProperty('description')) {
        // Return the value of the "description" key
        return error.response.data.description;
      }
      else if (error.response.data.hasOwnProperty('detail')) {
        // Return the value of the "description" key
        return `${error.response.data.detail[0].loc[1]}: ${error.response.data.detail[0].msg}`
      }

    }

    // Return null if the "description" key is not found or an error occurs
    return null;
  }

/**
 * Legacy function - kept for backwards compatibility
 * @deprecated Use getApiErrorMessage instead
 */
  export function getServerErrorMessage(error: Error): string {
    const errorMessage = getErrorMessage(error);
    if (errorMessage) {
      return 'Server Error: ' + errorMessage;
    } else {
      return 'Unknown Server Error: ' + error;
    }
}

/**
 * Legacy function - kept for backwards compatibility
 * @deprecated Use getApiErrorMessage instead
 */
export const handleError = (error: unknown): void => {
  console.error("Error occurred:");
  console.error(error);

  if (error instanceof Error && (error as any).response) {
    showErrorToastFullScreen(getServerErrorMessage(error));
  } else if (error instanceof Error && (error as any).request) {
    // Handle network error
    showErrorToastFullScreen('Network error. Please check your internet connection and try again.');
  } else {
    // Handle other errors
    showErrorToastFullScreen('An unexpected error occurred. Please try again later.');
  }
};

/**
 * Pull a single field's validation message out of a FastAPI 422 response.
 * Scans `error.response.data.detail` for the first entry whose `loc` array
 * ends in `field` (e.g. ["body","phone"]) and returns its `msg`. Returns
 * undefined for non-422s, missing/invalid detail, or no match.
 */
export function getFieldError(error: any, field: string): string | undefined {
  const detail = error?.response?.data?.detail;
  if (!Array.isArray(detail)) return undefined;
  const match = detail.find(
    (d: any) => Array.isArray(d?.loc) && d.loc[d.loc.length - 1] === field
  );
  return match?.msg;
}